import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  completeMyVenueOnboarding,
  fetchMyVenue,
  updateMyVenue,
  uploadMyVenueLogo,
} from "@/features/venues/api/venuesApi";
import type { UpdateVenueProfilePayload, VenueProfile } from "@/features/venues/types";

const QUERY_KEY = ["venue", "me"];

/** Venue profile fields (business_name, address, category, logo_url) are
 *  embedded in queries the worker and ops actors read. Mark those stale on
 *  every venue mutation so they refetch fresh data the next time they open
 *  the affected screen. See `frontend.md` rule #13. */
function invalidateCrossActorVenueViews(qc: QueryClient): void {
  // Worker side: shift feed + shift detail show venue_name + logo, the
  // My-applications view shows venue_name + venue_address.
  qc.invalidateQueries({ queryKey: ["shifts"] });
  qc.invalidateQueries({ queryKey: ["applications", "mine"] });
  // Ops side: the all-venues list, the venue dashboard summary, and the
  // upcoming-shifts board (groups by venue_name).
  qc.invalidateQueries({ queryKey: ["ops", "venues"] });
  qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
}

export function useVenueProfile() {
  return useQuery<VenueProfile>({ queryKey: QUERY_KEY, queryFn: fetchMyVenue });
}

export function useUpdateVenueProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateVenueProfilePayload) => updateMyVenue(payload),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      invalidateCrossActorVenueViews(qc);
    },
  });
}

export function useUploadVenueLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadMyVenueLogo(file),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      invalidateCrossActorVenueViews(qc);
    },
  });
}

export function useCompleteVenueOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: completeMyVenueOnboarding,
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      invalidateCrossActorVenueViews(qc);
    },
  });
}
